import React, { useEffect, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { ALL_STATES, UNKNOWN_STATE } from 'src/participant/participantConstants'
import { ParticipantIdentity } from '../useParticipantName'
import { advancedColors } from '../theme'
import { ageFrom, localTimeIn, zoneAbbreviationIn } from '../format'

const MINUTE_MS = 60_000

// Re-renders on each full minute so the participant's local time stays right.
const useMinuteClock = () => {
  const [, setTick] = useState(0)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    const timeout = setTimeout(() => {
      setTick((tick) => tick + 1)
      interval = setInterval(() => setTick((tick) => tick + 1), MINUTE_MS)
    }, MINUTE_MS - (Date.now() % MINUTE_MS))

    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [])
}

const noted = (title: string, text: string) => (
  <Tooltip title={title}>
    <Box component="span">{text}</Box>
  </Tooltip>
)

const SubtitleFacts = ({ identity }: { identity: ParticipantIdentity }) => {
  useMinuteClock()

  const age = ageFrom(identity.birthDate)
  const localTime = localTimeIn(identity.timezone)
  const zone = zoneAbbreviationIn(identity.timezone)
  const state = ALL_STATES.find((candidate) => candidate.code === identity.stateOfResidence)

  const ageValue =
    age === null ? noted(identity.birthDate ? 'Invalid birthdate' : 'Birthdate not set', '—') : `${age} yo`

  let stateValue: React.ReactNode = noted('State of residence not set', '—')
  if (state && state.code !== UNKNOWN_STATE.code) {
    stateValue = `${state.name} (${state.code})`
  } else if (identity.stateOfResidence && identity.stateOfResidence !== UNKNOWN_STATE.code) {
    stateValue = `Unknown (${identity.stateOfResidence})`
  }

  const timeValue = localTime
    ? noted(`In participant's timezone${zone ? ` (${zone})` : ''}`, localTime)
    : noted('User timezone not set', '—')

  return (
    <Typography component="p" variant="subtitle1" sx={{ mt: 0.5, mb: 0, color: advancedColors.inkMuted }}>
      {ageValue}
      <Box component="span" sx={{ mx: 0.75 }}>
        ·
      </Box>
      {stateValue}
      <Box component="span" sx={{ mx: 0.75 }}>
        ·
      </Box>
      {timeValue}
    </Typography>
  )
}

export default SubtitleFacts
